import {
  AddressSetting,
  OPTIMIZER_SETTING_CATEGORY,
  PlatformSyncStatus,
  SyncDotStatus,
  UpsertListingEntry,
} from 'src/shared/services/listing-optimization-settings.service';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

// ---------- Types ----------

/** The tiers the API's `cancellation_policy` row accepts — `limited` was retired. */
export type CancellationPolicy = 'flexible' | 'moderate' | 'firm' | 'strict';

/**
 * Instant Book is stored as ONE mode, not a toggle plus sub-toggles.
 *
 * Airbnb folds the whole feature onto a single booking-category enum (off / everyone /
 * well-reviewed guests) and rejects an `enabled` flag that disagrees with the mode, so the
 * backend accepts only the mode. The two surviving UI toggles map onto it:
 *
 *   off                       -> request_to_book
 *   on                        -> instant
 *   on + require track record -> conditional_instant
 *
 * "Require verified government ID" and "Require profile photo" were removed: they exist on
 * no connected channel, so no toggle could ever have taken effect.
 */
export type InstantBookMode = 'request_to_book' | 'instant' | 'conditional_instant';

/**
 * The values the channels actually accept, mirrored here so a bad number is caught at
 * the form instead of failing the whole save.
 *
 * Advance notice: Airbnb also takes same-day and 7 days, but Vrbo reads its own list per
 * property and offered neither — these three are the overlap. Preparation time: Airbnb's
 * picker offers exactly these, and it is the only channel with the setting at all.
 */
export const ADVANCE_NOTICE_DAYS: readonly number[] = [1, 2, 3];
export const PREPARATION_TIME_NIGHTS: readonly number[] = [0, 1, 2];

/**
 * "Restricted check-in / check-out days" is ONE control covering two stored settings —
 * the channel restricts arrivals and departures separately. The control writes the same
 * weekday set to both, and reads back the union, so a set restricted through the API on
 * only one of them still shows here. Splitting the control in two is the follow-up if
 * hosts need them to differ.
 *
 * The channels speak upper-case three-letter codes; the control uses title case.
 */
export const RESTRICTED_DAY_KEYS = ['closedToArrivalWeekdays', 'closedToDepartureWeekdays'] as const;

export function toWeekdayCodes(days: Record<string, boolean>): string[] {
  return Object.keys(days).filter(day => days[day]).map(day => day.toUpperCase());
}

export function toInstantBookMode(on: boolean, requireTrackRecord: boolean): InstantBookMode {
  if (!on) { return 'request_to_book'; }
  return requireTrackRecord ? 'conditional_instant' : 'instant';
}

export function fromInstantBookMode(mode: unknown): { instantBook: boolean; instantTrackRecord: boolean } {
  return {
    instantBook: mode === 'instant' || mode === 'conditional_instant',
    instantTrackRecord: mode === 'conditional_instant',
  };
}

/**
 * How the default stay-length is chosen. The three presets patch fixed
 * min/max-night values and lock the inputs; `custom` unlocks them so the user
 * can enter their own. The selection itself is persisted (in the `rental_types`
 * blob) alongside the resulting min/max nights.
 */
export type StayType = 'short' | 'mid' | 'long' | 'custom';

/** Fixed min/max nights each non-custom stay-type patches into the inputs. */
export const STAY_PRESETS: Record<Exclude<StayType, 'custom'>, { min: number; max: number }> = {
  short: { min: 1, max: 30 },
  mid: { min: 8, max: 60 },
  long: { min: 31, max: 365 },
};

/**
 * Ranges the API validates. A value outside these 400s the ENTIRE batch
 * (nothing saves), so the client never sends one.
 */
export const STAY_LIMITS = {
  minStay: { min: 1, max: 180 },
  maxStay: { min: 7, max: 365 },
  windowMinNights: { min: 1, max: 180 },
  windowMaxNights: { min: 7, max: 365 },
} as const;

/** The only non-null values `max_booking_advance_months` accepts. */
export const BOOKING_ADVANCE_MONTHS: readonly number[] = [6, 9, 12, 24];

/**
 * Serializable snapshot of the Strategy / Stay Length sub-component: the stay
 * type + its default min/max nights, how far ahead guests may book (months),
 * and optional date windows that override the min/max for specific date ranges.
 */
export interface RentalTypesState {
  stayType: StayType;
  minNights: number | null;
  maxNights: number | null;
  maxBookingAdvanceMonths: number | null;
  windows: Array<{ id: string; name: string; start: string; end: string; minNights: number | null; maxNights: number | null }>;
}

/**
 * VRBO's predefined seasonal-policy tiers — the only ones its cancellation page can
 * take. Deliberately NOT the CancellationPolicy set: `flexible` has no VRBO
 * projection (its Airbnb meaning is a 1-day window, nothing like VRBO's 14-day
 * relaxed), while `relaxed`/`non_refundable` exist only here.
 */
export const SEASONAL_POLICIES = ['relaxed', 'moderate', 'firm', 'strict', 'non_refundable'] as const;
export type SeasonalPolicy = typeof SEASONAL_POLICIES[number];

/**
 * One VRBO seasonal cancellation window. `id` is client-side only, for list
 * tracking — the API rejects unknown fields, so it never serializes.
 */
export interface SeasonalWindowState {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  policy: SeasonalPolicy | '';
}

export interface DiscountSetting { on: boolean; pct: number | null; }
export interface DiscountWithDays extends DiscountSetting { days: number | null; }

/** The seven discount levers, in the order they appear on the page. */
export const DISCOUNT_LEVERS = [
  'weekly', 'monthly', 'lastMinute', 'farOut', 'memberOnly', 'mobile', 'flash',
] as const;

export type DiscountLever = typeof DISCOUNT_LEVERS[number];

/**
 * Wire key per lever. Each lever is ONE `optimizer` row whose `data` carries the
 * whole thing (`{ enabled, value, days? }`) — it used to be two or three flat
 * `*_enabled`/`*_pct`/`*_days` rows, which a deploy-time rake removes.
 */
export const DISCOUNT_KEYS: Record<DiscountLever, string> = {
  weekly: 'discount_weekly',
  monthly: 'discount_monthly',
  lastMinute: 'discount_last_minute',
  farOut: 'discount_far_out',
  memberOnly: 'discount_member_only',
  mobile: 'discount_mobile',
  flash: 'discount_flash',
};

export interface DiscountBounds {
  /** User-facing lever name, used to build validation messages. */
  label: string;
  /** Inclusive percent range. */
  min: number;
  max: number;
  /** Present only on the two booking-window levers. `step` forces multiples. */
  days?: { min: number; max: number; step?: number };
}

/**
 * The intersection of what each supporting platform natively accepts — one
 * stored value is pushed unchanged to all of them, so the narrowest platform
 * wins. Enforced server-side; mirrored here because the page saves every lever
 * in one batch and a single out-of-range value 400s the WHOLE save.
 *
 * Only checked when the lever is enabled: a stale out-of-range percent left in
 * the form must never block turning a discount off. `0` always passes.
 */
export const DISCOUNT_BOUNDS: Record<DiscountLever, DiscountBounds> = {
  weekly: { label: 'Weekly stays', min: 1, max: 99 },
  monthly: { label: 'Monthly stays', min: 1, max: 99 },
  lastMinute: { label: 'Last-minute booking', min: 1, max: 75, days: { min: 1, max: 28 } },
  farOut: { label: 'Far-out booking', min: 1, max: 99, days: { min: 30, max: 480, step: 30 } },
  memberOnly: { label: 'Member-only exclusive', min: 10, max: 73 },
  mobile: { label: 'Mobile-only rate', min: 10, max: 75 },
  flash: { label: 'Flash / campaign deal', min: 1, max: 99 },
};

/**
 * `bookingForm` control backing each lever, so a validation failure can point at
 * the field that caused it instead of leaving the customer to hunt down the page.
 */
export const DISCOUNT_CONTROLS: Record<DiscountLever, { pct: string; days?: string }> = {
  weekly: { pct: 'discountWeekly' },
  monthly: { pct: 'discountMonthly' },
  lastMinute: { pct: 'discountLastMinute', days: 'discountLastMinuteDays' },
  farOut: { pct: 'discountFarOut', days: 'discountFarOutDays' },
  memberOnly: { pct: 'discountMemberOnly' },
  mobile: { pct: 'discountMobile' },
  flash: { pct: 'discountFlash' },
};

/** Every persisted Optimizer-page setting. Excludes the read-only rank tracker. */
export interface OptimizerSettings {
  // `securityDeposit` (dollars) rides the optimizer-defaults rows like the
  // price bounds, but writes on its own path: upsert when set, DELETE when
  // cleared (the backend rejects a null upsert). Direct booking only.
  price: { basePrice: number | null; minPrice: number | null; maxPrice: number | null; securityDeposit: number | null };
  channel: {
    instantBook: boolean;
    instantTrackRecord: boolean;
    nonRefundableRate: boolean;
    seasonalCancellation: SeasonalWindowState[];
  };
  discounts: {
    weekly: DiscountSetting;
    monthly: DiscountSetting;
    lastMinute: DiscountWithDays;
    farOut: DiscountWithDays;
    memberOnly: DiscountSetting;
    mobile: DiscountSetting;
    flash: DiscountSetting;
  };
  minStay: {
    advanceNotice: number | null;
    prepTime: number | null;
    checkInAfter: string;
    checkOutBy: string;
    restrictedDays: Record<string, boolean>;
  };
  listing: {
    petFriendly: boolean;
    petFee: number | null;
    cancellationPolicy: CancellationPolicy | '';
  };
  rentalTypes: RentalTypesState;
}

/**
 * Every Optimizer-page setting is stored as its OWN `optimizer`-category
 * address_settings row (gotidy: "don't group settings inside a blob, we have
 * category for grouping"). One key per leaf setting; two structured leaves
 * (`restricted_days`, `rental_types`) keep an array/object value since they
 * represent a single logical setting, not a grouping of unrelated ones.
 *
 * The seven discount levers follow the same rule but live in DISCOUNT_KEYS:
 * each is ONE row carrying `{ enabled, value, days? }`, because the platforms
 * accept a lever as a unit and the backend validates it as one.
 *
 * `base_price`/`min_price`/`max_price` are intentionally ABSENT here — the
 * price range rides its own validated/PriceLabs-synced write path
 * (ListingOptimizationSettingsService.upsertOptimizerDefaults), also under the
 * `optimizer` category, so it must not be double-written from the model.
 */
export const OPTIMIZER_SETTING_KEYS = {
  // Channel. Instant Book is ONE key: the channels fold the on/off switch and the
  // track-record requirement onto a single native enum and reject a combination that
  // disagrees with itself, so the two UI toggles serialize to one mode — see
  // InstantBookMode and toInstantBookMode at the top of this file.
  instantBookMode: 'instant_book_mode',
  nonRefundableRate: 'non_refundable_rate',
  seasonalCancellation: 'seasonal_cancellation',
  // Discounts live in DISCOUNT_KEYS — one row per lever, not one per field.
  // Minimum-stay / booking rules
  minStay: 'min_stay',
  maxStay: 'max_stay',
  advanceNotice: 'advance_notice_days',
  prepTime: 'prep_time',
  maxBookingAdvanceMonths: 'max_booking_advance_months',
  checkInAfter: 'check_in_after',
  checkOutBy: 'check_out_by',
  // One control, two stored settings — see RESTRICTED_DAY_KEYS below.
  closedToArrivalWeekdays: 'closed_to_arrival_weekdays',
  closedToDepartureWeekdays: 'closed_to_departure_weekdays',
  // Listing
  petsAllowed: 'pets_allowed',
  petFee: 'pet_fee',
  cancellationPolicy: 'cancellation_policy',
  // Strategy / rental types (structured value)
  rentalTypes: 'rental_types',
  overrideStayLengthByDateRange: 'override_stay_length_by_date_range',
} as const;

// ---------- Platform coverage ----------

export const PLATFORMS = ['airbnb', 'vrbo', 'booking_com'] as const;
export type Platform = typeof PLATFORMS[number];

export const PLATFORM_LABELS: Record<Platform, string> = {
  airbnb: 'Airbnb',
  vrbo: 'VRBO',
  booking_com: 'Booking.com',
};

/**
 * The channels the address can actually push to, from its listings as the API lists
 * them (deleted rows are already excluded there). Mirrors `AddressListing.connected`:
 * a listing still `connecting` is not a sync target.
 */
export function connectedPlatformsFrom(listings: ReadonlyArray<{ platform?: string; status?: string }>): Platform[] {
  const found = new Set<string>();
  for (const listing of listings) {
    if (listing.status !== 'connecting' && listing.platform) { found.add(listing.platform); }
  }
  return PLATFORMS.filter(p => found.has(p));
}

/** Rows the listing builder saves itself; its embedded Optimizer copy must not write them over the builder's edit. */
export const BUILDER_OWNED_KEYS: readonly string[] = [OPTIMIZER_SETTING_KEYS.petsAllowed, OPTIMIZER_SETTING_KEYS.cancellationPolicy];

export function withoutBuilderOwnedKeys(entries: readonly UpsertListingEntry[]): UpsertListingEntry[] {
  return entries.filter(e => !BUILDER_OWNED_KEYS.includes(e.key));
}

/** Keys whose row is new or carries different data than before — what a save actually changed. */
export function changedSettingKeys(before: readonly UpsertListingEntry[], after: readonly UpsertListingEntry[]): string[] {
  const previous = new Map(before.map(e => [e.key, JSON.stringify(e.data)]));
  return after.filter(e => previous.get(e.key) !== JSON.stringify(e.data)).map(e => e.key);
}

const NO_LISTING_ANYWHERE = 'No listing connected — saved, but won’t take effect.';
const NO_LISTING_ON: Record<Platform, string> = {
  airbnb: 'No Airbnb listing connected — saved, but won’t take effect.',
  vrbo: 'No VRBO listing connected — saved, but won’t take effect.',
  booking_com: 'No Booking.com listing connected — saved, but won’t take effect.',
};
const NO_LISTING_ON_SOME = 'No listing on some of these channels — saved, but won’t take effect there.';

/**
 * The warning to show after a save that landed but changed a setting none of whose
 * channels has a listing — the API stores it and never pushes it, so from the
 * customer's side the edit did nothing. Null when every changed setting reaches at
 * least one connected channel, or when the connected set isn't known yet.
 */
export function noListingWarning(changedKeys: readonly string[], connected: readonly Platform[] | null): string | null {
  if (!connected) { return null; }
  const stranded = changedKeys
    .map(key => PLATFORM_COVERAGE[key])
    .filter((channels): channels is readonly Platform[] => !!channels && !channels.some(p => connected.includes(p)));
  if (!stranded.length) { return null; }
  if (!connected.length) { return NO_LISTING_ANYWHERE; }
  const platforms = PLATFORMS.filter(p => stranded.some(channels => channels.includes(p)));
  return platforms.length === 1 ? NO_LISTING_ON[platforms[0]] : NO_LISTING_ON_SOME;
}

/**
 * Which OTA channels each setting the page sends is pushed to — a mirror of the
 * backend's `AddressSetting::SYNC_MATRIX` `channels`, which is the source of truth.
 * The page can't derive this from sync data: a row that has never synced carries no
 * `platform_sync`, and a setting's coverage is fixed regardless of what synced.
 *
 * Read by `<platform-pills>`, which marks a setting reaching a strict subset of the
 * three channels and stays silent for one reaching all of them. Keys the page does
 * not send (the PriceLabs-only price range) are absent. The
 * Listing Builder's content fields ride here too; store-only ones (place_type, …) are absent.
 */
export const PLATFORM_COVERAGE: Record<string, readonly Platform[]> = {
  title: ['airbnb', 'vrbo'],
  description: ['airbnb', 'vrbo'],
  max_guests: ['airbnb', 'vrbo'],
  photos: ['airbnb', 'vrbo'],
  spaces: ['airbnb', 'vrbo'],
  amenities: ['airbnb', 'vrbo', 'booking_com'],
  security_deposit: ['vrbo'],
  house_rules: ['vrbo'],
  instant_book_mode: ['airbnb', 'vrbo'],
  // VRBO models no-refund as a cancellation tier instead (seasonal_cancellation).
  non_refundable_rate: ['airbnb', 'booking_com'],
  seasonal_cancellation: ['vrbo'],
  min_stay: ['airbnb', 'vrbo', 'booking_com'],
  max_stay: ['airbnb', 'vrbo'],
  max_booking_advance_months: ['airbnb', 'vrbo'],
  discount_weekly: ['airbnb', 'vrbo', 'booking_com'],
  discount_monthly: ['airbnb', 'vrbo', 'booking_com'],
  discount_last_minute: ['airbnb', 'vrbo', 'booking_com'],
  discount_far_out: ['airbnb', 'vrbo', 'booking_com'],
  discount_member_only: ['vrbo', 'booking_com'],
  discount_mobile: ['vrbo', 'booking_com'],
  discount_flash: ['booking_com'],
  check_in_after: ['airbnb', 'vrbo'],
  check_out_by: ['airbnb', 'vrbo'],
  closed_to_arrival_weekdays: ['airbnb'],
  closed_to_departure_weekdays: ['airbnb'],
  advance_notice_days: ['airbnb', 'vrbo'],
  prep_time: ['airbnb'],
  pets_allowed: ['airbnb', 'vrbo', 'booking_com'],
  pet_fee: ['vrbo'],
  cancellation_policy: ['airbnb', 'vrbo', 'booking_com'],
};

// ---------- Empty / blank state ----------
// A real account with NO saved listing settings starts completely empty: blank
// numeric inputs (null), no price range, no date windows, and every toggle OFF.
// (Demo accounts never use this — they keep their own hardcoded mock values.)

export const DEFAULT_OPTIMIZER_SETTINGS: OptimizerSettings = {
  price: { basePrice: null, minPrice: null, maxPrice: null, securityDeposit: null },
  channel: {
    instantBook: false,
    instantTrackRecord: false,
    nonRefundableRate: false,
    seasonalCancellation: [],
  },
  discounts: {
    weekly: { on: false, pct: null },
    monthly: { on: false, pct: null },
    lastMinute: { on: false, pct: null, days: null },
    farOut: { on: false, pct: null, days: null },
    memberOnly: { on: false, pct: null },
    mobile: { on: false, pct: null },
    flash: { on: false, pct: null },
  },
  minStay: {
    advanceNotice: null,
    prepTime: null,
    checkInAfter: '',
    checkOutBy: '',
    restrictedDays: { Sun: false, Mon: false, Tue: false, Wed: false, Thu: false, Fri: false, Sat: false },
  },
  listing: {
    petFriendly: false,
    petFee: null,
    cancellationPolicy: '',
  },
  rentalTypes: {
    // Defaults to the Short preset so a fresh account shows/persists a
    // consistent stay length (the toggle defaults to Short too).
    stayType: 'short',
    minNights: STAY_PRESETS.short.min,
    maxNights: STAY_PRESETS.short.max,
    maxBookingAdvanceMonths: null,
    windows: [],
  },
};

/** Parse a form value to a number, treating blank/invalid as null (not 0). */
export function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') { return null; }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// ---------- Validation ----------
// Every stay setting is validated server-side against the shared platform
// ranges, and a single bad value 400s the WHOLE batch atomically. Mirroring the
// rules here keeps one invalid field from blocking the rest of the page.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `tidy-datepicker` writes a JS Date into its control, while the window editors
 * validate and serialize `yyyy-mm-dd`. Local date parts, not toISOString: the
 * calendar day the customer picked must not shift with their UTC offset.
 */
export function toIsoDate(value: unknown): string {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }
  return typeof value === 'string' ? value : '';
}

function inRange(v: number | null, r: { min: number; max: number }): boolean {
  return v !== null && Number.isInteger(v) && v >= r.min && v <= r.max;
}

/** A validation message for one date window, keyed by window id. */
export interface WindowError { id: string; message: string; }

/** `null` (not set) or one of the four accepted month counts. */
export function isValidBookingAdvance(months: number | null): boolean {
  return months === null || BOOKING_ADVANCE_MONTHS.includes(months);
}

/** Booking.com's rate plans refuse a default minimum stay above this. */
export const BOOKING_COM_MAX_MIN_STAY = 28;

export const BOOKING_COM_MIN_STAY_WARNING =
  'Booking.com takes at most 28 nights as its minimum, so this minimum will show as not supported on Booking.com.';

/** A minimum the other channels take but Booking.com refuses still saves. */
export function minStayWarning(rt: RentalTypesState): string | null {
  return typeof rt.minNights === 'number' && rt.minNights > BOOKING_COM_MAX_MIN_STAY
    ? BOOKING_COM_MIN_STAY_WARNING
    : null;
}

/**
 * Address defaults require BOTH nights present, each inside the shared platform
 * range, with minimum strictly lower than maximum.
 */
export function stayLengthError(rt: RentalTypesState): string | null {
  const { minStay, maxStay } = STAY_LIMITS;
  if (!inRange(rt.minNights, minStay)) {
    return `Min nights must be a whole number from ${minStay.min} to ${minStay.max}.`;
  }
  if (!inRange(rt.maxNights, maxStay)) {
    return `Max nights must be a whole number from ${maxStay.min} to ${maxStay.max}.`;
  }
  if ((rt.minNights as number) >= (rt.maxNights as number)) {
    return 'Min nights must be lower than max nights.';
  }
  if (!isValidBookingAdvance(rt.maxBookingAdvanceMonths)) {
    return `How far ahead guests can book must be ${BOOKING_ADVANCE_MONTHS.join(', ')} months.`;
  }
  return null;
}

/**
 * Each window needs a unique non-empty id, a non-empty name, ISO dates with
 * `start` on or before `end`, in-range night overrides, and no overlap with
 * another window. At most one message per window — the first problem found.
 */
export function validateWindows(windows: RentalTypesState['windows']): WindowError[] {
  const { windowMinNights, windowMaxNights } = STAY_LIMITS;
  const errors = new Map<string, string>();
  const fail = (id: string, message: string) => { if (!errors.has(id)) { errors.set(id, message); } };
  const seenIds = new Set<string>();

  for (const w of windows) {
    if (!w.id) { continue; }
    if (seenIds.has(w.id)) { fail(w.id, 'Each date window needs its own name and dates.'); }
    seenIds.add(w.id);

    if (!w.name?.trim()) { fail(w.id, 'Give this window a name.'); }
    if (!ISO_DATE.test(w.start) || !ISO_DATE.test(w.end)) { fail(w.id, 'Pick a start and end date.'); }
    else if (w.start > w.end) { fail(w.id, 'The start date must be on or before the end date.'); }

    if (w.minNights !== null && !inRange(w.minNights, windowMinNights)) {
      fail(w.id, `Min nights must be from ${windowMinNights.min} to ${windowMinNights.max}.`);
    }
    if (w.maxNights !== null && !inRange(w.maxNights, windowMaxNights)) {
      fail(w.id, `Max nights must be from ${windowMaxNights.min} to ${windowMaxNights.max}.`);
    }
    if (w.minNights !== null && w.maxNights !== null && w.minNights >= w.maxNights) {
      fail(w.id, 'Min nights must be lower than max nights.');
    }
  }

  // Overlap is only meaningful between windows whose dates already parse.
  // ISO dates compare correctly as plain strings.
  const dated = windows
    .filter(w => w.id && ISO_DATE.test(w.start) && ISO_DATE.test(w.end) && w.start <= w.end)
    .sort((a, b) => a.start.localeCompare(b.start));
  for (let i = 1; i < dated.length; i++) {
    if (dated[i].start <= dated[i - 1].end) {
      fail(dated[i].id, overlapMessage(dated[i - 1].name));
    }
  }

  return [...errors].map(([id, message]) => ({ id, message }));
}

/**
 * Mirrors the backend's own rules (which mirror the PMS's): ISO dates with
 * `startDate` on or before `endDate`, start not in the past (UTC), an inclusive
 * span of at most 730 days, a policy from the VRBO set, and no overlaps. The
 * save is atomic, so one bad window would 400 every other setting on the page.
 *
 * The past-start rule is for creating a window, not keeping one: a window that
 * matches one in `kept` (what was loaded or last saved) skips it, exactly as the
 * backend does — otherwise every window blocks the whole page the day after it
 * starts.
 */
export function validateSeasonalWindows(windows: SeasonalWindowState[], kept: SeasonalWindowState[] = []): WindowError[] {
  const errors = new Map<string, string>();
  const fail = (id: string, message: string) => { if (!errors.has(id)) { errors.set(id, message); } };
  const todayUtc = new Date().toISOString().slice(0, 10);
  const keptKeys = new Set(kept.map(seasonalWindowKey));

  for (const w of windows) {
    if (!w.policy) { fail(w.id, 'Pick a cancellation policy for this window.'); }
    if (!ISO_DATE.test(w.startDate) || !ISO_DATE.test(w.endDate)) { fail(w.id, 'Pick a start and end date.'); }
    else if (w.startDate > w.endDate) { fail(w.id, 'The start date must be on or before the end date.'); }
    else {
      if (w.startDate < todayUtc && !keptKeys.has(seasonalWindowKey(w))) {
        fail(w.id, 'The start date must be today or later.');
      }
      const span = (Date.parse(`${w.endDate}T00:00:00Z`) - Date.parse(`${w.startDate}T00:00:00Z`)) / 86400000 + 1;
      if (span > 730) { fail(w.id, 'A window can span at most 730 days.'); }
    }
  }

  const dated = windows
    .filter(w => ISO_DATE.test(w.startDate) && ISO_DATE.test(w.endDate) && w.startDate <= w.endDate)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  for (let i = 1; i < dated.length; i++) {
    if (dated[i].startDate <= dated[i - 1].endDate) {
      fail(dated[i].id, overlapMessage(dated[i - 1].name));
    }
  }

  return [...errors].map(([id, message]) => ({ id, message }));
}

/**
 * The overlap message quotes the other window's name — customer data — so it can never
 * be one translation key; the sentence around the name is translated as fragments.
 */
function overlapMessage(otherName: string): string {
  const t = (text: string) => new TranslationPipe().transform(text);
  return `${t('These dates overlap')} “${otherName || t('another window')}”.`;
}

/** A window as the API stores it — what "already stored as-is" compares. */
function seasonalWindowKey(w: SeasonalWindowState): string {
  return JSON.stringify([w.name.trim(), w.startDate, w.endDate, w.policy]);
}

/** True when the whole strategy card is safe to send. */
export function isValidRentalTypes(rt: RentalTypesState): boolean {
  return stayLengthError(rt) === null && validateWindows(rt.windows).length === 0;
}

// ---------- Serialize (model -> upsert entries) ----------

/**
 * Flattens the model into one `optimizer`-category row per leaf setting (see
 * OPTIMIZER_SETTING_KEYS). No blob — the backend groups by the `category`
 * column. `restrictedDays` serializes to the array of restricted weekday names;
 * `rentalTypes` keeps its structured object (one logical setting).
 *
 * `price` is intentionally excluded — the base/min/max range is written on its
 * own validated, PriceLabs-synced path (upsertOptimizerDefaults), so a mirror
 * here could shadow the authoritative values the day-detail panel also reads.
 *
 * The seven discount levers are appended one row each; the stay-length group
 * rides `stayLengthEntries` (see there for why it is all-or-nothing).
 */
export function toEntries(m: OptimizerSettings): UpsertListingEntry[] {
  const K = OPTIMIZER_SETTING_KEYS;
  const entry = (key: string, value: unknown): UpsertListingEntry => ({ key, data: { value } });
  const restrictedDays = toWeekdayCodes(m.minStay.restrictedDays);
  const entries: UpsertListingEntry[] = [
    // Channel
    entry(K.instantBookMode, toInstantBookMode(m.channel.instantBook, m.channel.instantTrackRecord)),
    // Sent on every save; the API re-pushes a row to the platforms only when its value changes.
    entry(K.nonRefundableRate, m.channel.nonRefundableRate),
    // Misc / booking rules
    entry(K.closedToArrivalWeekdays, restrictedDays),
    entry(K.closedToDepartureWeekdays, restrictedDays),
    // Listing
    entry(K.petsAllowed, m.listing.petFriendly),
  ];
  // Synced keys the API refuses a blank value for (`configured?`), and a rejected row
  // 400s the whole batch — so an unset value is an ABSENT row, which `fromSettings`
  // reads back as blank. (Weekday lists above keep `[]`: an empty list clears the
  // restriction, which the API accepts as a real value.)
  if (m.minStay.advanceNotice !== null) {entries.push(entry(K.advanceNotice, m.minStay.advanceNotice));}
  if (m.minStay.prepTime !== null) {entries.push(entry(K.prepTime, m.minStay.prepTime));}
  if (m.minStay.checkInAfter !== '') {entries.push(entry(K.checkInAfter, m.minStay.checkInAfter));}
  if (m.minStay.checkOutBy !== '') {entries.push(entry(K.checkOutBy, m.minStay.checkOutBy));}
  if (m.listing.petFee !== null) {
    // The page edits whole dollars; the API stores (and VRBO receives) cents.
    entries.push(entry(K.petFee, Math.round(m.listing.petFee * 100)));
  }
  if (m.listing.cancellationPolicy) {
    entries.push(entry(K.cancellationPolicy, m.listing.cancellationPolicy));
  }
  // An empty array is a real value (it clears every VRBO seasonal window), so unlike the
  // dated stay overrides this always sends. The page blocks the save on an invalid window
  // first (validateSeasonalCancellation) — the save is atomic, and one bad window would
  // 400 every other setting on the page.
  entries.push(entry(K.seasonalCancellation, m.channel.seasonalCancellation.map(toSeasonalWindowPayload)));
  // Discounts — one row per lever, each carrying the whole lever.
  for (const lever of DISCOUNT_LEVERS) {
    entries.push({ key: DISCOUNT_KEYS[lever], data: discountData(m.discounts[lever], lever) });
  }
  return [...entries, ...stayLengthEntries(m.rentalTypes)];
}

/**
 * The stay-length group is all-or-nothing.
 *
 * The API cross-checks the `rental_types` row against the dedicated rows —
 * its `min_stay`/`max_stay` must equal the `min_stay`/`max_stay` rows — and
 * rejects the ENTIRE batch on a mismatch. So the old trick of dropping a blank
 * `min_stay` row while still sending the blob is exactly the mismatch that 400s
 * every other setting on the page.
 *
 * When the strategy card is incomplete or out of range we therefore send none of
 * these rows, leaving the last-saved values in place while the rest of the page
 * keeps saving normally. The card renders the same validation message, so the
 * omission is never silent.
 *
 * `max_booking_advance_months` is omitted when unset (an address that never
 * stored one): the API accepts only 6/9/12/24, 400s on null, and the key is in
 * the backend's SYNC_MATRIX so it can never be deleted — omission just leaves
 * the stored value untouched.
 */
function stayLengthEntries(rt: RentalTypesState): UpsertListingEntry[] {
  if (!isValidRentalTypes(rt)) { return []; }
  const K = OPTIMIZER_SETTING_KEYS;
  const entry = (key: string, value: unknown): UpsertListingEntry => ({ key, data: { value } });
  const entries = [
    entry(K.minStay, rt.minNights),
    entry(K.maxStay, rt.maxNights),
    entry(K.rentalTypes, toRentalTypesPayload(rt)),
  ];
  // Only send the dated overrides when there are windows — sending `[]` on every save is
  // a pointless write. (Clearing the last window relies on a separate delete/dirty path.)
  if (rt.windows.length > 0) {
    entries.push(entry(K.overrideStayLengthByDateRange, rt.windows.map(toWindowPayload)));
  }
  if (rt.maxBookingAdvanceMonths !== null) {
    entries.push(entry(K.maxBookingAdvanceMonths, rt.maxBookingAdvanceMonths));
  }
  return entries;
}

/**
 * The `rental_types` wire payload — EXACTLY `stay_type`, `min_stay`, `max_stay`.
 * The API asserts the field set exactly, rejecting unknown and missing names
 * alike, so the page's internal shape (camelCase, plus the windows and the
 * booking-advance months that each own their own row now) can't be sent as-is.
 */
function toRentalTypesPayload(rt: RentalTypesState): Record<string, unknown> {
  return { stay_type: rt.stayType, min_stay: rt.minNights, max_stay: rt.maxNights };
}

/** One dated stay-length override, in the same exact-field style. */
function toWindowPayload(w: RentalTypesState['windows'][number]): Record<string, unknown> {
  return {
    id: w.id,
    name: w.name,
    start: w.start,
    end: w.end,
    min_stay: w.minNights,
    max_stay: w.maxNights,
  };
}

/**
 * `name` is the one optional field and a blank one is a 400 (non-empty when
 * present), so it's omitted rather than sent empty. No `id`: the API rejects
 * unknown fields.
 */
function toSeasonalWindowPayload(w: SeasonalWindowState): Record<string, unknown> {
  const name = w.name.trim();
  return {
    ...(name ? { name } : {}),
    start_date: w.startDate,
    end_date: w.endDate,
    policy: w.policy,
  };
}

/**
 * Rebuilds the editor's windows from the stored row. Tolerates the key's legacy
 * boolean payload (the old toggle) and any malformed entry by dropping them —
 * they were silent no-ops, not values worth preserving.
 */
function seasonalWindowsFromValue(value: unknown): SeasonalWindowState[] {
  if (!Array.isArray(value)) { return []; }
  const windows: SeasonalWindowState[] = [];
  for (const item of value) {
    if (item === null || typeof item !== 'object') { continue; }
    const w = item as Record<string, unknown>;
    if (typeof w['start_date'] !== 'string' || typeof w['end_date'] !== 'string') { continue; }
    if (!SEASONAL_POLICIES.includes(w['policy'] as SeasonalPolicy)) { continue; }
    windows.push({
      id: `sw-${windows.length + 1}`,
      name: typeof w['name'] === 'string' ? w['name'] : '',
      startDate: w['start_date'],
      endDate: w['end_date'],
      policy: w['policy'] as SeasonalPolicy,
    });
  }
  return windows;
}

/**
 * One lever's `data` payload. `enabled` is required and must be a real boolean;
 * `value` is an integer percent that may be null. `days` is sent ONLY for the
 * two levers that define it — including it on any other lever is a 400 listing
 * the unknown fields — and is omitted rather than sent as null, since the
 * contract only documents null for `value`.
 */
function discountData(d: DiscountSetting | DiscountWithDays, lever: DiscountLever): Record<string, unknown> {
  const data: Record<string, unknown> = { enabled: !!d.on, value: d.pct };
  if (DISCOUNT_BOUNDS[lever].days) {
    const days = (d as DiscountWithDays).days;
    if (days !== null && days !== undefined) { data['days'] = days; }
  }
  return data;
}

// ---------- Deserialize (settings rows -> model) ----------

/**
 * Reads the individual `optimizer`-category rows back into the model, each field
 * falling back to its default when the row is absent (forward-compat for fields
 * added later). Price rows (base/min/max) are ignored here — they hydrate via
 * getOptimizerDefaults in the page.
 */
export function fromSettings(settings: AddressSetting[] | undefined): OptimizerSettings {
  const K = OPTIMIZER_SETTING_KEYS;
  const d = DEFAULT_OPTIMIZER_SETTINGS;
  // Whole `data` object per key — discount levers need every field of it, not
  // just `value`.
  const rows = new Map<string, Record<string, unknown>>();
  for (const s of settings ?? []) {
    if (s.category === OPTIMIZER_SETTING_CATEGORY) {
      rows.set(s.key, s.data ?? {});
    }
  }
  const values = new Map<string, unknown>(
    Array.from(rows, ([key, data]) => [key, data['value']])
  );
  /** Reads one lever back out of its single row; an absent row reads as off/blank. */
  const discount = (lever: DiscountLever): DiscountSetting => {
    const data = rows.get(DISCOUNT_KEYS[lever]) ?? {};
    return {
      on: data['enabled'] === true,
      pct: typeof data['value'] === 'number' ? data['value'] : null,
    };
  };
  /** Same, for the two levers that also carry a booking window. */
  const discountWithDays = (lever: DiscountLever): DiscountWithDays => {
    const data = rows.get(DISCOUNT_KEYS[lever]) ?? {};
    return {
      ...discount(lever),
      days: typeof data['days'] === 'number' ? data['days'] : null,
    };
  };
  const bool = (key: string, fallback: boolean): boolean => {
    const v = values.get(key);
    return typeof v === 'boolean' ? v : fallback;
  };
  const num = (key: string): number | null => {
    const v = values.get(key);
    return typeof v === 'number' ? v : null;
  };
  const str = (key: string, fallback: string): string => {
    const v = values.get(key);
    return typeof v === 'string' ? v : fallback;
  };

  const model = mergeWithDefaults({
    channel: {
      ...(values.has(K.instantBookMode)
        ? fromInstantBookMode(values.get(K.instantBookMode))
        : { instantBook: d.channel.instantBook, instantTrackRecord: d.channel.instantTrackRecord }),
      nonRefundableRate: bool(K.nonRefundableRate, d.channel.nonRefundableRate),
      seasonalCancellation: seasonalWindowsFromValue(values.get(K.seasonalCancellation)),
    },
    discounts: {
      weekly: discount('weekly'),
      monthly: discount('monthly'),
      lastMinute: discountWithDays('lastMinute'),
      farOut: discountWithDays('farOut'),
      memberOnly: discount('memberOnly'),
      mobile: discount('mobile'),
      flash: discount('flash'),
    },
    minStay: {
      advanceNotice: num(K.advanceNotice),
      prepTime: num(K.prepTime),
      checkInAfter: str(K.checkInAfter, d.minStay.checkInAfter),
      checkOutBy: str(K.checkOutBy, d.minStay.checkOutBy),
      restrictedDays: restrictedDaysFromValue(
        values.get(K.closedToArrivalWeekdays),
        values.get(K.closedToDepartureWeekdays)
      ),
    },
    listing: {
      petFriendly: bool(K.petsAllowed, d.listing.petFriendly),
      petFee: petFeeDollars(num(K.petFee)),
      cancellationPolicy: cancellationPolicyFromValue(values.get(K.cancellationPolicy)),
    },
    // Reassembled from the two rows the API stores it across; mergeRentalTypes
    // validates the raw shape, so the cast is safe.
    rentalTypes: rentalTypesFromRows(
      values.get(K.rentalTypes),
      values.get(K.overrideStayLengthByDateRange)
    ) as unknown as RentalTypesState | undefined,
  });

  // The dedicated min_stay/max_stay/max_booking_advance_months rows are
  // authoritative for the Strategy card (they're what the backend validates and
  // other consumers read); let a present row win over the blob's mirror, so
  // existing accounts written before the blob carried these still hydrate
  // correctly.
  const minStayVal = num(K.minStay);
  const maxStayVal = num(K.maxStay);
  const advanceVal = num(K.maxBookingAdvanceMonths);
  if (minStayVal !== null) { model.rentalTypes.minNights = minStayVal; }
  if (maxStayVal !== null) { model.rentalTypes.maxNights = maxStayVal; }
  if (advanceVal !== null) { model.rentalTypes.maxBookingAdvanceMonths = advanceVal; }
  // A rental whose nights were set before the stay type existed has no stored
  // stay_type. Defaulting it to Short made the card patch 1-30 over the stored
  // nights, and any save then pushed 1-30 to the channels (#19537).
  const storedStayType = (values.get(K.rentalTypes) as Record<string, unknown> | undefined)?.['stay_type'];
  if (!isStayType(storedStayType) && (minStayVal !== null || maxStayVal !== null)) {
    model.rentalTypes.stayType = stayTypeForNights(minStayVal, maxStayVal);
  }
  return model;
}

/** The preset whose nights match exactly, else `custom` so the stored nights are kept. */
function stayTypeForNights(minNights: number | null, maxNights: number | null): StayType {
  const preset = (Object.keys(STAY_PRESETS) as Array<keyof typeof STAY_PRESETS>)
    .find(type => STAY_PRESETS[type].min === minNights && STAY_PRESETS[type].max === maxNights);
  return preset ?? 'custom';
}

/**
 * Rebuilds the weekday->boolean record from the two stored arrays. Reads the UNION: one
 * control stands for both settings, so a day restricted on either side shows as selected.
 * Accepts the channels' upper-case codes and the title-case names older rows stored.
 */
function restrictedDaysFromValue(...values: unknown[]): Record<string, boolean> {
  const base = { ...DEFAULT_OPTIMIZER_SETTINGS.minStay.restrictedDays };
  const byCode = new Map(Object.keys(base).map(day => [day.toUpperCase(), day]));
  for (const value of values) {
    if (!Array.isArray(value)) { continue; }
    for (const day of value) {
      if (typeof day !== 'string') { continue; }
      const name = byCode.get(day.toUpperCase());
      if (name) { base[name] = true; }
    }
  }
  return base;
}

/**
 * How the discount levers are landing on the channels, or '' when everything is
 * settled.
 *
 * A channel that REJECTED a write is called out separately from one still working
 * through it. Both keep retrying (1, 5 and 15 minutes, then a daily sweep), so neither
 * is final — but a rejection usually means the channel connection needs attention,
 * and it used to be indistinguishable from a healthy in-flight write.
 *
 * All phrasings are fixed strings rather than counts: TranslationPipe looks up whole
 * strings with no interpolation, so an embedded number could never be translated.
 */
/**
 * The single status a per-field sync dot shows, aggregated over every platform (and
 * every backing setting key) of that field:
 *   - `partial` (orange) — a platform failed but another synced: a partial success.
 *   - `failed`  (red)    — a platform failed and none synced.
 *   - `pending` (yellow) — nothing failed and a platform is still in flight (a synced
 *                          alongside a pending stays pending).
 *   - `synced`  (green)  — every platform synced.
 *   - `not_supported` (grey) — the only connected channels can't take the setting.
 *   - `null`             — nothing to show; the field reaches no channel (empty
 *                          `platform_sync`, e.g. seasonal_cancellation or rental_types).
 *
 * @param maps one `platform_sync` map per backing key (a row can cover more than one, e.g.
 *   the check-in/out times or the two closed-to weekday lists)
 */
export function aggregateSyncStatus(
  maps: Array<Partial<Record<string, PlatformSyncStatus>> | undefined>
): SyncDotStatus | null {
  const values = maps.flatMap(map => Object.values(map ?? {}));
  const hasFailed = values.includes('failed');
  const hasSynced = values.includes('synced');
  if (hasFailed) { return hasSynced ? 'partial' : 'failed'; }
  if (values.includes('pending')) { return 'pending'; }
  if (hasSynced) { return 'synced'; }
  if (values.includes('not_supported')) { return 'not_supported'; }
  return null;
}

/**
 * The platforms (slugs) whose sync landed in `status`, across all a field's backing
 * keys, de-duplicated and in first-seen order — for the dot's tooltip.
 *
 * @param maps one `platform_sync` map per backing key
 * @param status the per-platform status to collect
 */
function platformsWithSyncStatus(
  maps: Array<Partial<Record<string, PlatformSyncStatus>> | undefined>,
  status: PlatformSyncStatus
): string[] {
  const out: string[] = [];
  for (const map of maps) {
    for (const [platform, value] of Object.entries(map ?? {})) {
      if (value === status && !out.includes(platform)) { out.push(platform); }
    }
  }
  return out;
}

/** Platforms that failed to sync — for the "Failed on …" tooltip. */
export function failedSyncPlatforms(
  maps: Array<Partial<Record<string, PlatformSyncStatus>> | undefined>
): string[] {
  return platformsWithSyncStatus(maps, 'failed');
}

/** Platforms that synced — for the partial dot's "Synced on …" tooltip. */
export function syncedSyncPlatforms(
  maps: Array<Partial<Record<string, PlatformSyncStatus>> | undefined>
): string[] {
  return platformsWithSyncStatus(maps, 'synced');
}

/** Platforms whose push is still in flight — for the tooltip's "Syncing to …" rows. */
export function pendingSyncPlatforms(
  maps: Array<Partial<Record<string, PlatformSyncStatus>> | undefined>
): string[] {
  return platformsWithSyncStatus(maps, 'pending');
}

/** Connected platforms the setting can't be pushed to — for the "Not supported on …" rows. */
export function unsupportedSyncPlatforms(
  maps: Array<Partial<Record<string, PlatformSyncStatus>> | undefined>
): string[] {
  return platformsWithSyncStatus(maps, 'not_supported');
}

/** What a platform said about a push it refused, and which parts of it it would not take. */
export interface PlatformSyncError {
  message: string;
  /** Present when the platform took the field but refused parts of it, e.g. single photos. */
  refused?: Array<{ field?: string; sourceId?: string; reason?: string }>;
}

/** The first push error per platform, over a field's backing keys — for the "Failed on …" tooltip. */
export function syncFailureReasons(
  errorMaps: Array<Record<string, PlatformSyncError> | undefined>
): Record<string, string> {
  const reasons: Record<string, string> = {};
  for (const map of errorMaps) {
    for (const [platform, error] of Object.entries(map ?? {})) {
      if (error?.message) {reasons[platform] ??= error.message;}
    }
  }
  return reasons;
}

/** The parts each platform refused, over a field's backing keys — for flagging single photos. */
export function syncRefusedParts(
  errorMaps: Array<Record<string, PlatformSyncError> | undefined>
): Record<string, NonNullable<PlatformSyncError['refused']>> {
  const refused: Record<string, NonNullable<PlatformSyncError['refused']>> = {};
  for (const map of errorMaps) {
    for (const [platform, error] of Object.entries(map ?? {})) {
      if (error?.refused?.length) {refused[platform] ??= error.refused;}
    }
  }
  return refused;
}

/**
 * What each refused photo should say, keyed by our own photo id. The platform echoes back the
 * `sourceId` we sent it (ListingPhoto.id), which is how a refusal finds its tile in the gallery;
 * an entry without one — a gallery saved before photos carried ids — has no tile to sit on and
 * stays in the field-level message.
 */
export function refusedPhotoReasons(
  errorMaps: Array<Record<string, PlatformSyncError> | undefined>
): Record<string, Array<{ platform: string; reason: string }>> {
  const byPhoto: Record<string, Array<{ platform: string; reason: string }>> = {};
  for (const [platform, parts] of Object.entries(syncRefusedParts(errorMaps))) {
    for (const part of parts) {
      if (!part.sourceId || !part.reason) {continue;}
      (byPhoto[part.sourceId] ??= []).push({ platform, reason: part.reason });
    }
  }
  return byPhoto;
}

/** Every channel a field can sync to, over its backing keys — for the not-supported tooltip. */
export function coveragePlatforms(keys: string[]): Platform[] {
  return PLATFORMS.filter(platform => keys.some(key => PLATFORM_COVERAGE[key]?.includes(platform)));
}

/**
 * Reads the same per-platform `platform_sync` the lever dots read (aggregateSyncStatus),
 * not the row's `synced` flag — the two can disagree, and a note that contradicts the
 * dots next to it is worse than none. A lever with no platform state (nothing to push
 * to) doesn't count.
 */
export function buildDiscountSyncNote(settings: AddressSetting[] | undefined): string {
  const keys = new Set(Object.values(DISCOUNT_KEYS));
  const statuses = (settings ?? [])
    .filter(s => s.category === OPTIMIZER_SETTING_CATEGORY && keys.has(s.key))
    .map(s => aggregateSyncStatus([s.platform_sync]))
    .filter((status): status is SyncDotStatus => status !== null && status !== 'synced' && status !== 'not_supported');
  if (!statuses.length) { return ''; }

  if (statuses.some(status => status === 'failed' || status === 'partial')) {
    return 'A channel rejected a discount — check your channel connections.';
  }
  return statuses.length === 1
    ? 'One discount is still syncing to your channels.'
    : 'Some discounts are still syncing to your channels.';
}

// ---------- Validation ----------

/** One problem, tied to the form control that caused it so the field can be marked. */
export interface OptimizerValidationError {
  /** `bookingForm` control name — see DISCOUNT_CONTROLS and the price fields. */
  field: string;
  message: string;
}

/**
 * Mirrors the server-side bounds so a bad value is caught before the PUT. This
 * matters because the page batches every lever into one request and the backend
 * rejects the batch atomically, so one out-of-range percent would otherwise
 * take every other edit on the page down with it.
 *
 * Returns EVERY problem (not just the first) so the page can mark all the
 * offending fields at once, leaving the user one pass to fix them instead of
 * save-fix-save. An empty array means the model is safe to send.
 *
 * Deliberately permissive where the backend is: a percent of 0 is a real
 * setting meaning "no discount", so it always passes.
 *
 * Note the asymmetry the server applies, mirrored here — a disabled lever skips
 * the RANGE check but is still TYPE-checked. The percent inputs are numeric, so
 * a fractional value is enterable, and it would 400 the whole batch even with
 * the lever switched off.
 *
 * Stay length is NOT validated here: the address-level min/max night rules ship
 * with the stay-optimizations work, which owns those bounds and the
 * `max_booking_advance_months` rename.
 */
export function validateOptimizerSettings(m: OptimizerSettings): OptimizerValidationError[] {
  const errors: OptimizerValidationError[] = [];
  for (const lever of DISCOUNT_LEVERS) {
    const bounds = DISCOUNT_BOUNDS[lever];
    const controls = DISCOUNT_CONTROLS[lever];
    const d = m.discounts[lever] as DiscountWithDays;

    if (d.pct !== null && !Number.isInteger(d.pct)) {
      errors.push({ field: controls.pct, message: `${bounds.label}: enter a whole number.` });
    }
    if (bounds.days && controls.days && d.days !== null && !Number.isInteger(d.days)) {
      errors.push({ field: controls.days, message: `${bounds.label}: enter a whole number of days.` });
    }
    if (!d.on) { continue; }

    // An enabled lever with no percent saves happily and then discounts
    // nothing, so the customer sees an active discount that does nothing.
    if (d.pct === null) {
      errors.push({ field: controls.pct, message: `${bounds.label}: enter a discount % or turn it off.` });
    } else if (d.pct !== 0 && (d.pct < bounds.min || d.pct > bounds.max)) {
      errors.push({ field: controls.pct, message: `${bounds.label}: must be between ${bounds.min}% and ${bounds.max}%.` });
    }

    if (bounds.days && controls.days && d.days !== null && Number.isInteger(d.days)) {
      const { min, max, step } = bounds.days;
      if (d.days < min || d.days > max) {
        errors.push({ field: controls.days, message: `${bounds.label}: window must be between ${min} and ${max} days.` });
      } else if (step && d.days % step !== 0) {
        errors.push({ field: controls.days, message: `${bounds.label}: window must be a multiple of ${step} days.` });
      }
    }
  }

  // Booking.com applies the deeper deal to 28+ night stays where Airbnb and
  // VRBO apply the monthly one, so a monthly below weekly would behave
  // differently per platform. Blamed on the monthly field — that's the one the
  // server names and the one the customer has to raise.
  const { weekly, monthly } = m.discounts;
  if (weekly.on && monthly.on && (weekly.pct ?? 0) > 0 && (monthly.pct ?? 0) > 0 && monthly.pct! < weekly.pct!) {
    errors.push({
      field: DISCOUNT_CONTROLS.monthly.pct,
      message: 'Monthly stays: must be at least the weekly discount.',
    });
  }

  return errors;
}

/**
 * Blocks the save when the Strategy card can't be sent.
 *
 * `stayLengthEntries` drops the whole stay group when it's invalid, so without
 * this the batch succeeds, the page reports success, and the stay change is
 * silently gone on reload — the card's inline message is visible but nothing
 * stops the save.
 *
 * The per-window detail stays inline: an overlap message quotes the window's
 * name, which is customer data and so could never be a translation key.
 */
export function validateStayLength(rt: RentalTypesState): OptimizerValidationError[] {
  const stay = stayLengthError(rt);
  if (stay) { return [{ field: 'minNights', message: stay }]; }
  if (validateWindows(rt.windows).length) {
    return [{ field: 'windows', message: 'Fix the highlighted date windows before saving.' }];
  }
  return [];
}

/**
 * Same shape for the VRBO seasonal windows: `toEntries` omits the row while any
 * window is invalid, so without this the save 200s and the window is silently
 * gone on reload.
 */
export function validateSeasonalCancellation(windows: SeasonalWindowState[], kept: SeasonalWindowState[] = []): OptimizerValidationError[] {
  if (validateSeasonalWindows(windows, kept).length) {
    return [{ field: 'seasonalWindows', message: 'Fix the highlighted seasonal cancellation windows before saving.' }];
  }
  return [];
}

/**
 * Flags a price bound the customer has blanked out.
 *
 * There is no way to remove a stored bound: the backend requires `data` on
 * every entry (so `null` no longer soft-deletes) and its delete endpoint
 * explicitly refuses the three price keys because they are platform-synced.
 * Silently dropping the field from the payload would show a success toast and
 * then restore the old value on reload, so the clear is rejected up front
 * instead.
 *
 * @param stored the values as last loaded or saved; null means nothing is stored yet
 */
export function validatePriceBounds(
  price: OptimizerSettings['price'],
  stored: OptimizerSettings['price'] | null
): OptimizerValidationError[] {
  if (!stored) { return []; }
  const fields: Array<[keyof OptimizerSettings['price'], string, string]> = [
    ['basePrice', 'basePrice', 'Base price'],
    ['minPrice', 'minPrice', 'Minimum price'],
    ['maxPrice', 'maxPrice', 'Maximum price'],
  ];
  return fields
    .filter(([key]) => price[key] === null && stored[key] !== null)
    .map(([, field, label]) => ({
      field,
      message: `${label}: can't be removed once set — enter a value.`,
    }));
}

/** Returns a fresh, ref-independent copy of the default settings. */
export function defaultSettings(): OptimizerSettings {
  return mergeWithDefaults({});
}

// Structured merge — every level returns new objects/arrays so the result never
// shares references with DEFAULT_OPTIMIZER_SETTINGS (the page mutates these in place).
function mergeWithDefaults(raw: Partial<OptimizerSettings>): OptimizerSettings {
  const d = DEFAULT_OPTIMIZER_SETTINGS;
  const r = (raw ?? {}) as any;
  return {
    price: { ...d.price, ...(r.price ?? {}) },
    channel: { ...d.channel, ...(r.channel ?? {}) },
    discounts: {
      weekly: { ...d.discounts.weekly, ...(r.discounts?.weekly ?? {}) },
      monthly: { ...d.discounts.monthly, ...(r.discounts?.monthly ?? {}) },
      lastMinute: { ...d.discounts.lastMinute, ...(r.discounts?.lastMinute ?? {}) },
      farOut: { ...d.discounts.farOut, ...(r.discounts?.farOut ?? {}) },
      memberOnly: { ...d.discounts.memberOnly, ...(r.discounts?.memberOnly ?? {}) },
      mobile: { ...d.discounts.mobile, ...(r.discounts?.mobile ?? {}) },
      flash: { ...d.discounts.flash, ...(r.discounts?.flash ?? {}) },
    },
    minStay: {
      ...d.minStay,
      ...(r.minStay ?? {}),
      restrictedDays: { ...d.minStay.restrictedDays, ...(r.minStay?.restrictedDays ?? {}) },
    },
    listing: { ...d.listing, ...(r.listing ?? {}) },
    rentalTypes: mergeRentalTypes(r.rentalTypes),
  };
}

/**
 * Rebuilds the page's rental-types shape from the two rows the API stores it
 * across: `rental_types` (`stay_type`/`min_stay`/`max_stay`) and the dated
 * overrides in `override_stay_length_by_date_range`. Mirrors the write mapping;
 * without it a saved strategy reads back as blank, since nothing on the wire is
 * camelCase any more.
 */
function rentalTypesFromRows(rentalTypes: unknown, windows: unknown): Record<string, unknown> | undefined {
  const raw = (rentalTypes && typeof rentalTypes === 'object')
    ? rentalTypes as Record<string, unknown>
    : undefined;
  if (!raw && !Array.isArray(windows)) { return undefined; }
  const minNights = raw?.['min_stay'] ?? null;
  const maxNights = raw?.['max_stay'] ?? null;
  return {
    stayType: raw?.['stay_type'],
    minNights,
    maxNights,
    windows: Array.isArray(windows)
      ? windows.map((w: any) => ({
          id: w?.id,
          name: w?.name,
          start: w?.start,
          end: w?.end,
          minNights: w?.min_stay ?? null,
          maxNights: w?.max_stay ?? null,
        }))
      : [],
  };
}

function mergeRentalTypes(r: any): RentalTypesState {
  const d = DEFAULT_OPTIMIZER_SETTINGS.rentalTypes;
  if (!r || typeof r !== 'object') {
    return {
      stayType: d.stayType,
      minNights: d.minNights,
      maxNights: d.maxNights,
      maxBookingAdvanceMonths: d.maxBookingAdvanceMonths,
      windows: d.windows.map(w => ({ ...w })),
    };
  }
  return {
    stayType: isStayType(r.stayType) ? r.stayType : d.stayType,
    minNights: numOrNull(r.minNights),
    maxNights: numOrNull(r.maxNights),
    maxBookingAdvanceMonths: numOrNull(r.maxBookingAdvanceMonths),
    // An explicitly-empty windows array is respected (user deleted all windows);
    // only a missing/invalid value falls back to the defaults.
    windows: Array.isArray(r.windows)
      ? r.windows.map((w: any) => ({
          id: String(w.id),
          name: w.name ?? '',
          start: w.start ?? '',
          end: w.end ?? '',
          minNights: numOrNull(w.minNights),
          maxNights: numOrNull(w.maxNights),
        }))
      : d.windows.map(w => ({ ...w })),
  };
}

function isStayType(v: unknown): v is StayType {
  return v === 'short' || v === 'mid' || v === 'long' || v === 'custom';
}

/** Stored in cents; the page edits whole dollars. */
function petFeeDollars(cents: number | null): number | null {
  return cents === null ? null : cents / 100;
}

/** Drops a tier the API no longer accepts (e.g. the retired `limited`). */
function cancellationPolicyFromValue(value: unknown): CancellationPolicy | '' {
  const allowed: CancellationPolicy[] = ['flexible', 'moderate', 'firm', 'strict'];
  return allowed.includes(value as CancellationPolicy) ? value as CancellationPolicy : '';
}
